import React from 'react';
import {
  Card,
  Stack,
  Text,
  Group,
  Badge,
  Title,
  Divider,
  ThemeIcon,
  Progress,
  Timeline,
  Button,
  SimpleGrid,
  Paper
} from '@mantine/core';
import {
  IconMoodSmile,
  IconMoodSad,
  IconMoodNeutral,
  IconMoodHappy,
  IconMoodEmpty,
  IconToolsKitchen2,
  IconMoon,
  IconClock,
  IconActivity,
  IconDownload,
  IconPrinter,
  IconCalendar
} from '@tabler/icons-react';
import type { DailyReport } from '../../../types/api';

interface DailyReportCardProps {
  report: DailyReport;
  onDownload?: () => void;
  onPrint?: () => void;
}

const getMoodIcon = (mood: string) => {
  switch (mood) {
    case 'happy':
    case 'excited':
      return IconMoodHappy;
    case 'sad':
    case 'upset':
      return IconMoodSad;
    case 'neutral':
      return IconMoodNeutral;
    case 'tired':
      return IconMoodEmpty;
    default:
      return IconMoodSmile;
  }
};

const getMoodColor = (mood: string) => {
  switch (mood) {
    case 'happy':
    case 'excited':
      return 'green';
    case 'sad':
    case 'upset':
      return 'red';
    case 'neutral':
      return 'gray';
    case 'tired':
      return 'orange';
    default:
      return 'blue';
  }
};

const getMealAmount = (amount?: string) => {
  switch (amount) {
    case 'all':
      return { label: 'All', value: 100, color: 'green' };
    case 'some':
      return { label: 'Some', value: 75, color: 'blue' };
    case 'little':
      return { label: 'Little', value: 25, color: 'orange' };
    case 'none':
      return { label: 'None', value: 0, color: 'red' };
    default:
      return { label: 'Not recorded', value: 0, color: 'gray' };
  }
};

export const DailyReportCard: React.FC<DailyReportCardProps> = ({
  report,
  onDownload,
  onPrint
}) => {
  const MoodIcon = getMoodIcon(report.mood || 'neutral');
  const moodColor = getMoodColor(report.mood || 'neutral');

  const formatTime = (time?: string) => {
    if (!time) return 'Not recorded';

    // Handle different time formats
    try {
      // If it's already in HH:MM format, just return it formatted
      if (/^\d{1,2}:\d{2}$/.test(time)) {
        const [hours, minutes] = time.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${displayHour}:${minutes} ${ampm}`;
      }

      // If it's already formatted (like "8:30 AM"), return as is
      if (/^\d{1,2}:\d{2}\s?(AM|PM)$/i.test(time)) {
        return time;
      }

      // Try to parse as a full date/time string
      const date = new Date(time);
      if (!isNaN(date.getTime())) {
        return date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit'
        });
      }

      // If all else fails, return the original string
      return time;
    } catch (error) {
      console.warn('Error formatting time:', time, error);
      return time || 'Not recorded';
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Card withBorder>
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Group gap="xs" mb="xs">
              <IconCalendar size={20} />
              <Title order={3}>Daily Report</Title>
            </Group>
            <Text size="sm" c="dimmed">{formatDate(report.date)}</Text>
          </div>
          <Group gap="xs">
            <Button
              size="xs"
              variant="light"
              leftSection={<IconDownload size={14} />}
              onClick={onDownload}
            >
              Download
            </Button>
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPrinter size={14} />}
              onClick={onPrint}
            >
              Print
            </Button>
          </Group>
        </Group>

        <Divider />

        {/* Attendance & Mood */}
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="md">
          <Paper p="md" withBorder>
            <Group gap="xs" mb="xs">
              <ThemeIcon size="sm" variant="light" color="green">
                <IconClock size={14} />
              </ThemeIcon>
              <Text size="sm" fw={500}>Arrival</Text>
            </Group>
            <Text size="lg" fw={600}>{formatTime(report.arrival_time)}</Text>
          </Paper>

          <Paper p="md" withBorder>
            <Group gap="xs" mb="xs">
              <ThemeIcon size="sm" variant="light" color="blue">
                <IconClock size={14} />
              </ThemeIcon>
              <Text size="sm" fw={500}>Departure</Text>
            </Group>
            <Text size="lg" fw={600}>{formatTime(report.departure_time)}</Text>
          </Paper>

          <Paper p="md" withBorder>
            <Group gap="xs" mb="xs">
              <ThemeIcon size="sm" variant="light" color={moodColor}>
                <MoodIcon size={14} />
              </ThemeIcon>
              <Text size="sm" fw={500}>Overall Mood</Text>
            </Group>
            <Badge size="lg" color={moodColor} variant="light">
              {report.mood ? (report.mood.charAt(0).toUpperCase() + report.mood.slice(1)) : 'Unknown'}
            </Badge>
          </Paper>

          <Paper p="md" withBorder>
            <Group gap="xs" mb="xs">
              <ThemeIcon size="sm" variant="light" color="teal">
                <IconActivity size={14} />
              </ThemeIcon>
              <Text size="sm" fw={500}>Activities</Text>
            </Group>
            <Text size="lg" fw={600}>{report.total_activities || 0}</Text>
          </Paper>
        </SimpleGrid>

        {/* Meals */}
        <Card withBorder p="md">
          <Group gap="xs" mb="md">
            <IconToolsKitchen2 size={20} />
            <Text fw={600}>Meals</Text>
          </Group>
          <Stack gap="md">
            {['breakfast', 'lunch', 'snack'].map((mealType) => {
              const meal = report.meals?.[mealType as keyof typeof report.meals];
              if (!meal) return null;
              const mealInfo = getMealAmount(meal.amount);

              return (
                <div key={mealType}>
                  <Group justify="space-between" mb="xs">
                    <Text size="sm" fw={500} tt="capitalize">{mealType}</Text>
                    <Badge size="sm" color={mealInfo.color} variant="light">
                      {mealInfo.label}
                    </Badge>
                  </Group>
                  <Progress
                    value={mealInfo.value}
                    color={mealInfo.color}
                    size="sm"
                    radius="xl"
                  />
                  {meal.notes && (
                    <Text size="xs" c="dimmed" mt="xs">{meal.notes}</Text>
                  )}
                </div>
              );
            })}
          </Stack>
        </Card>

        {/* Nap Time */}
        <Card withBorder p="md">
          <Group gap="xs" mb="md">
            <IconMoon size={20} />
            <Text fw={600}>Nap Time</Text>
          </Group>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
            <div>
              <Text size="xs" c="dimmed">Start Time</Text>
              <Text fw={500}>{formatTime(report.nap?.start_time)}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">End Time</Text>
              <Text fw={500}>{formatTime(report.nap?.end_time)}</Text>
            </div>
            <div>
              <Text size="xs" c="dimmed">Quality</Text>
              <Badge
                color={report.nap?.quality === 'excellent' ? 'green' :
                       report.nap?.quality === 'good' ? 'blue' :
                       report.nap?.quality === 'fair' ? 'orange' : 'red'}
                variant="light"
              >
                {report.nap?.quality ? (
                  report.nap.quality.replace('_', ' ').charAt(0).toUpperCase() +
                  report.nap.quality.slice(1).replace('_', ' ')
                ) : 'Not recorded'}
              </Badge>
            </div>
          </SimpleGrid>
          {report.nap?.notes && (
            <Text size="sm" c="dimmed" mt="md">{report.nap.notes}</Text>
          )}
        </Card>

        {/* Activities Timeline */}
        {report.activities && report.activities.length > 0 && (
          <Card withBorder p="md">
            <Text fw={600} mb="md">Today's Activities</Text>
            <Timeline bulletSize={24}>
              {report.activities.slice(0, 5).map((activity) => (
                <Timeline.Item
                  key={activity.id}
                  title={activity.activity}
                  bullet={<IconActivity size={12} />}
                >
                  <Text size="xs" c="dimmed">
                    {formatTime(activity.time)} • Classroom
                  </Text>
                  {activity.notes && (
                    <Text size="xs" c="dimmed" mt={4}>{activity.notes}</Text>
                  )}
                </Timeline.Item>
              ))}
            </Timeline>
            {report.activities.length > 5 && (
              <Text size="sm" c="dimmed" ta="center" mt="md">
                And {report.activities.length - 5} more activities...
              </Text>
            )}
          </Card>
        )}

        {/* Teacher's Summary */}
        {report.summary && (
          <Card withBorder p="md" bg="gray.0">
            <Text fw={600} mb="md">Teacher's Summary</Text>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {report.summary}
            </Text>
            {report.teacher_id && (
              <Text size="xs" c="dimmed" mt="md" ta="right">
                - Teacher {report.teacher_id}
              </Text>
            )}
          </Card>
        )}
      </Stack>
    </Card>
  );
};

export default DailyReportCard;
